import type { Audience } from './schema';

/**
 * Role tiers. Resolved at login from the signed-in Contact's Zoho `Diocesan_Role`
 * per the instance config (e.g. liturgy: "Liturgical Commission" → commission),
 * then cached in the session. See ADR-006.
 */
export type Role = 'commission' | 'clergy' | 'public';

/**
 * Which commentary audiences each role may see. The **Worker** filters every
 * commentary read against this set before any payload leaves the server — a
 * clergy or public session must never receive `commission` commentary in any
 * response (handoff §7.3, the non-negotiable). The web layer uses the same map
 * only for presentation; it is never the gate.
 */
export const ROLE_AUDIENCES: Record<Role, readonly Audience[]> = {
  commission: ['commission', 'clergy', 'public'],
  clergy: ['clergy', 'public'],
  public: ['public'],
};

/** The audiences a role is entitled to see. */
export function visibleAudiences(role: Role): readonly Audience[] {
  return ROLE_AUDIENCES[role];
}

/** Whether a role may see a given commentary audience. */
export function canSee(role: Role, audience: Audience): boolean {
  return ROLE_AUDIENCES[role].includes(audience);
}

/** Only the Commission writes (edits structure or commentary). */
export function canWrite(role: Role): boolean {
  return role === 'commission';
}
